import { useState } from 'react';
import { IioTable } from '../../../components/IioTable';
import { useSearchTermLists, SearchTermList } from '../../../hooks/useSearchTerms';
import { FilterInput } from '../../../components/FilterInput';
import { LoadingPlaceholder } from '../../../components/LoadingPlaceholder';
import { formatLocaleDate } from '../../../utilities/date';
import { AddListModal } from './AddListModal';

// The API's createdAt is currently the response time, not the creation time; a Mongo
// ObjectId starts with its creation time in seconds. Drop once the backend is fixed.
function listCreatedAt(list: SearchTermList): number | string {
  return /^[0-9a-f]{24}$/i.test(list.id) ? parseInt(list.id.slice(0, 8), 16) * 1000 : list.createdAt;
}

interface Props {
  projectId: string;
  onOpen: (list: SearchTermList) => void;
}

export function ListsView({ projectId, onOpen }: Props) {
  const { data, isLoading } = useSearchTermLists(projectId);
  const [showModal, setShowModal] = useState(false);
  const [filterText, setFilterText] = useState('');

  const lists = (data?.searchTermLists ?? []).filter(l =>
    !filterText || l.name.toLowerCase().includes(filterText.toLowerCase())
  );

  return (
    <div>
      {showModal && (
        <AddListModal
          projectId={projectId}
          onClose={() => setShowModal(false)}
          onCreated={list => { setShowModal(false); onOpen(list); }}
        />
      )}

      <div className="row">
        <div className="col-md-8">
          <div className="btn-toolbar">
            <button className="btn btn-primary" onClick={() => setShowModal(true)} data-testid="admin-search-terms-new-btn">
              New Search Terms <i className="fas fa-plus-circle" aria-hidden="true" />
            </button>
            <FilterInput value={filterText} onChange={setFilterText} testId="admin-search-terms-filter-input" />
          </div>
        </div>
      </div>

      <div className="row" style={{ marginTop: 12 }}>
        <div className="col-md-6">
          {isLoading && <LoadingPlaceholder />}
          {!isLoading && lists.length === 0 && (
            <p className="text-muted">No search term lists yet.</p>
          )}
          {lists.length > 0 && (
            <IioTable data-testid="admin-search-terms-list-table">
              <colgroup>
                <col />
                <col />
                <col style={{ width: 150 }} />
              </colgroup>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Date</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {lists.map(list => (
                  <tr key={list.id} className={list.status === 'Running' ? 'active' : ''} data-testid="admin-search-terms-list-row">
                    <td>
                      <a
                        href="#"
                        data-testid="admin-search-terms-list-link"
                        onClick={e => { e.preventDefault(); onOpen(list); }}
                        className={list.status === 'Running' ? 'text-muted' : ''}
                      >
                        {list.name}
                      </a>
                    </td>
                    <td>{formatLocaleDate(listCreatedAt(list))}</td>
                    <td>{list.status}</td>
                  </tr>
                ))}
              </tbody>
            </IioTable>
          )}
        </div>
      </div>
    </div>
  );
}
